import type { Asset } from '../types/domain';
import { SUBJECT_CONFIG, isValidSubjectCode } from './subjectConfig';

const PAS_SHEET_CODE_RE = /PF-PAS-(\d{4})(\d{2})(\d{2})-\d{6}/i;

export function isImageLikeUrl(url?: string | null): boolean {
  if (!url) {
    return false;
  }

  const normalized = url.trim().toLowerCase();
  return normalized.startsWith('data:image/')
    || /\.(png|jpe?g|gif|webp|bmp|heic|heif)(\?|#|$)/i.test(normalized)
    || normalized.includes('x-oss-process=image')
    || normalized.includes('content-type=image');
}

export function isImageLikeAsset(asset: Pick<Asset, 'type' | 'url'>): boolean {
  const type = (asset.type || '').trim().toLowerCase();
  if (type.startsWith('image') || type === 'photo' || type === 'picture') {
    return true;
  }

  return isImageLikeUrl(asset.url);
}

export function formatDayKey(input: Date | string): string {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value || '';
  const month = parts.find((part) => part.type === 'month')?.value || '';
  const day = parts.find((part) => part.type === 'day')?.value || '';
  return `${year}-${month}-${day}`;
}

function harvestText(value: unknown, into: string[]): void {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) into.push(trimmed);
    return;
  }
  if (!value || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    for (const item of value) harvestText(item, into);
    return;
  }
  for (const item of Object.values(value as Record<string, unknown>)) {
    harvestText(item, into);
  }
}

export function readPasSheetDayKey(asset: {
  filename?: string | null;
  description?: string | null;
  ai_title?: string | null;
  metadata?: unknown;
}): string | null {
  const chunks: string[] = [];
  harvestText(asset.filename, chunks);
  harvestText(asset.description, chunks);
  harvestText(asset.ai_title, chunks);
  harvestText(asset.metadata, chunks);
  const match = chunks.join('\n').match(PAS_SHEET_CODE_RE);
  if (!match) return null;
  const dayKey = `${match[1]}-${match[2]}-${match[3]}`;
  const probe = new Date(`${dayKey}T00:00:00+08:00`);
  if (Number.isNaN(probe.getTime())) return null;
  return dayKey;
}

export function formatChineseDate(dayKey: string): string {
  const date = new Date(`${dayKey}T00:00:00`);
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
}

export function formatShortDateLabel(dayKey: string): string {
  const date = new Date(`${dayKey}T00:00:00`);
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}

export function formatDateTime(input?: string): string {
  if (!input) {
    return '时间未知';
  }

  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return '时间未知';
  }

  return `${date.getMonth() + 1}月${date.getDate()}日 ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

export function buildAssetSections(
  items: Asset[],
  columnCount: number,
  options?: { preferPasSheetDate?: boolean },
) {
  const grouped = new Map<string, Asset[]>();

  for (const asset of items) {
    const dayKey = (
      options?.preferPasSheetDate
        ? (readPasSheetDayKey(asset) || formatDayKey(asset.created_at))
        : formatDayKey(asset.created_at)
    ) || formatDayKey(asset.created_at);
    const bucket = grouped.get(dayKey);
    if (bucket) {
      bucket.push(asset);
    } else {
      grouped.set(dayKey, [asset]);
    }
  }

  return Array.from(grouped.entries())
    .sort((left, right) => (left[0] < right[0] ? 1 : left[0] > right[0] ? -1 : 0))
    .map(([dayKey, dayItems]) => ({
    dayKey,
    title: formatChineseDate(dayKey),
    count: dayItems.length,
    rows: Array.from({ length: Math.ceil(dayItems.length / columnCount) }, (_, rowIndex) => ({
      key: `${dayKey}-${rowIndex}`,
      items: dayItems.slice(rowIndex * columnCount, rowIndex * columnCount + columnCount),
    })),
  }));
}

export function resolveSubjectBadge(asset: Pick<Asset, 'subject' | 'ai_category'>) {
  const subjectCode = isValidSubjectCode(asset.subject)
    ? asset.subject
    : (Object.keys(SUBJECT_CONFIG) as Array<keyof typeof SUBJECT_CONFIG>).find(
        (code) => asset.ai_category && SUBJECT_CONFIG[code].label === asset.ai_category.trim(),
      ) ?? null;

  return subjectCode ? SUBJECT_CONFIG[subjectCode] : null;
}
